/***
 * Created on 2023/9/19
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useRef, useState} from 'react';
import ModalDialog from "../../common/modal_dialog/modalDialog";
import {useTranslation} from "react-i18next";
import {useTogglePopup} from "../../../hook/toggle_popup";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../../service/api";
import {useBoundedStore} from 'common/Store';
import {getErrorMessage} from 'common/ServiceCommon';
import {useCaptcha} from "../../../hook/captcha";


function AddPhoneWizardStep2({country_code, country, onSelectCountry, onChange, values, seconds, onSendMobile, ...props}) {
    const { t } = useTranslation();
    const {isShow, toggleAction, elemRef} = useTogglePopup();
    const [countDown, setCountDown] = useState(seconds || 0);
    const modalRef = useRef();
    const store = useBoundedStore();
    const queryClient = useQueryClient();
    const {Captcha, hasCaptcha, captchaValue, captchaError} = useCaptcha();

     useQuery({
        queryKey: ['get_countdown'],
        queryFn: async () => {
            const data = await apiGet('/ajax/countdown');
            const now = new Date().getTime();
            const value = Math.round((now - data?.countdown) / 1000) || 0;
            setCountDown(value > 60 ? 0 : 60 - value);
            return data;
        }
    });

    const verifyMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/verify_mobile', data),
        onSuccess: async data => {
            await queryClient.invalidateQueries({
                    queryKey: ['profile']
                });
            props.closeModal();
            store.globalAlert.setAlert(t('@You have successfully added your phone number to this QNAP account@'), 'success', true);
        },
        onError: (error, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: ['get_captcha_image']
            })
            // store.globalAlert.setAlert(getErrorMessage(t, error?.cause?.code), 'error', true);
        }
    });

    const onVerify = async () => {
        modalRef.current.alert('error', false, '');
        await verifyMutation.mutateAsync({
            country_code: values.country_code,
            mobile_number: values.number,
            code: values.code,
            captcha: captchaValue
        });
    }

    const resend = async () => {
        try {
            await onSendMobile();
        } catch (error) {
            modalRef.current.alert('error', true,  getErrorMessage(t, error.cause?.code));
        }
    }

    return (
        <ModalDialog {...props} title={t('@Add phone number@')} submitText={t('@Verify@')} submitAction={onVerify} ref={modalRef}>
            <div className="dialog-content">
                <>
                    <div className="dialog-content__pinned-msg dialog-content__pinned-msg--info mb-16">
                        {t('@Recommend you to finish the activation first then close this page.@')}
                    </div>

                    <div className="dialog__form-group">
                        <label>{t('@The SMS has been sent to@')}</label>
                        <div className="comp-input">
                            <div className="input-field--hybrid">
                                <div className={`comp-select ${isShow ? 'active' : ''}`} ref={elemRef}
                                     onClick={toggleAction}>
                                    <div className="q-select cursor-pointer">
                                        <div
                                            className="q-select__label">{country?.country}({country?.country_code})
                                        </div>
                                        <div className="q-select__arrow"></div>
                                    </div>
                                    <div className="comp-dropdown" data-modal-sheet>
                                        <ul className="dropdown-menu">
                                            <li className="dropdown-menu-section">
                                                {
                                                    country_code?.data && country_code.data.map(country =>
                                                        <div className="dropdown-menu__item cursor-pointer"
                                                             key={country._id}
                                                             onClick={() => onSelectCountry(country)}>{country.country}(+{country.country_code})</div>
                                                    )
                                                }
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <input type="text" name="number" value={values.number} onChange={onChange}/>
                                <button className="q-btn q-btn-default" disabled={seconds > 0}
                                        onClick={resend}>{`${t('@Send@')} ${seconds > 0 ? '(' + seconds + ')' : ''}`}</button>
                            </div>
                        </div>
                    </div>
                    <ol className="list-style-type-disc ml-16">
                        <li><span
                            className="text-note">{t('@Please note that the QNAP ID (phone number) can only be used on QTS 4.3.4 or above.@')}</span>
                        </li>
                        <li><span
                            className="text-note">{t('@The phone number will not be saved before finishing the activation progress.@')}</span>
                        </li>
                    </ol>

                    <div className="dialog__form-group">
                        <label>{t('@Please check your SMS and enter the verification code in the box below@')}</label>
                        <input type="text" name="code" value={values.code} onChange={onChange}
                               className="w-full max-w-full input--must-fill"/>
                    </div>

                    {hasCaptcha && Captcha }

                </>
            </div>
        </ModalDialog>
    );
}

export default AddPhoneWizardStep2;